import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getConsent, onCookieSettings, setConsent } from "@/lib/consent";
import { t } from "@/i18n";

export function CookieBanner({ storageKey = "inm_cookie_consent", text, accept, reject, privacyHref, privacyLabel, dark }) {
  const [show, setShow] = useState(false);
  const [full, setFull] = useState(false);
  useEffect(() => {
    const off = onCookieSettings(() => setShow(true)); // footer "Cookie settings" reopens the banner
    if (!getConsent(storageKey)) {
      const tk = setTimeout(() => setShow(true), 600);
      return () => { clearTimeout(tk); off(); };
    }
    return off;
  }, [storageKey]);
  const choose = (v) => {
    setConsent(v, storageKey);
    setShow(false);
  };
  // Accept and reject share one style and one size so neither choice is nudged (AEPD cookie guide).
  const btn = `min-w-[5.5rem] whitespace-nowrap rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors sm:min-w-[7rem] sm:px-4 sm:py-2 sm:text-sm ${dark ? "border-white/30 hover:bg-white/10" : "border-[#0B1A2E]/25 hover:bg-black/5"}`;
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          role="dialog" aria-label={t("Cookie consent")} data-testid="cookie-banner"
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className={`fixed inset-x-0 bottom-0 z-[1000] border-t px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 shadow-2xl sm:bottom-4 sm:left-4 sm:right-4 sm:mx-auto sm:max-w-xl sm:rounded-2xl sm:border sm:p-5 ${dark ? "glass-dark border-white/10 text-white" : "glass border-black/5 text-[#0B1A2E]"}`}
        >
          {/* Phones: a slim bar, the text clamped to two lines (tap it to read it all; screen readers always get the full text),
              the policy link and the two equal buttons on one row. From sm up: the floating card. */}
          <p onClick={() => setFull(true)} data-testid="cookie-text"
             className={`text-[12px] leading-snug sm:line-clamp-none sm:text-sm sm:leading-relaxed ${full ? "" : "line-clamp-2 cursor-pointer"}`}>
            {text}
          </p>
          <div className="mt-1.5 flex items-center gap-2 sm:mt-3">
            <Link to={privacyHref} className="mr-auto min-w-0 text-[12px] leading-tight underline underline-offset-2 sm:text-sm" data-testid="cookie-privacy-link">{privacyLabel}</Link>
            <div className="grid shrink-0 grid-cols-2 gap-2">{/* equal columns: both buttons are always the same width */}
              <button data-testid="cookie-accept-btn" onClick={() => choose("accepted")} className={btn}>{accept}</button>
              <button data-testid="cookie-reject-btn" onClick={() => choose("rejected")} className={btn}>{reject}</button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function Brand({ className = "", light }) {
  return (
    <span className={`inline-flex items-center gap-2 font-display text-[1.15rem] font-semibold tracking-tight ${className}`}>
      <svg width="26" height="26" viewBox="0 0 32 32" aria-hidden="true">
        <rect width="32" height="32" rx="8" fill={light ? "#F4EDE2" : "#0B1A2E"} />
        <path d="M9 22V12l7-5 7 5v10" fill="none" stroke={light ? "#0B1A2E" : "#F4EDE2"} strokeWidth="2.4" strokeLinejoin="round" />
        <circle cx="16" cy="18" r="2.6" fill="#0EA5A0" />
      </svg>
      InmoTera
    </span>
  );
}

export const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};
